/**
 * Who a cached session belongs to.
 *
 * `sessionStorage` is per-tab and survives a logout/login inside that
 * tab. The session cache used to be keyed only by
 * `(laravelSessionUrl, reportToken)`, so a second user logging into the
 * same tab restored the FIRST user's session token and queried Power BI
 * as them — answering over data they must not see (incident 2026-09-09).
 *
 * These helpers make a cached session provable: it is only restored for
 * the identity that minted it. Every ambiguous case resolves to "not
 * mine", which costs one round-trip to Laravel and never serves another
 * user's data.
 */

/** Claims hosts use for the user id, in the order we trust them. */
const ID_CLAIMS = ['sub', 'user_id', 'userId', 'id', 'email'] as const;

/**
 * A short, non-reversible id for the current user.
 *
 * Prefers a STABLE claim from the JWT so a routine token refresh keeps
 * the same fingerprint — otherwise every refresh would throw away a good
 * session. Falls back to the whole token when no claim is readable: we
 * then cannot tell who it is, so any different token counts as a
 * different user.
 *
 * Never returns the token or any part of it — this value is written to
 * `sessionStorage`, and a bearer must not be.
 */
export function sessionOwnerFingerprint(token: string): string {
  if (!token) return '';
  return hash(claimIdentity(token) ?? token);
}

/** Whether a cached entry may be restored for the current identity. */
export function isCacheOwnedBy(
  cached: { owner?: string | null },
  currentOwner: string,
): boolean {
  // An entry written before this fix carries no owner. Treating it as
  // "probably mine" would keep the leak alive for the lifetime of every
  // tab already open, so it is discarded.
  if (!cached?.owner || !currentOwner) return false;
  return cached.owner === currentOwner;
}

function claimIdentity(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as Record<string, unknown>;
    for (const claim of ID_CLAIMS) {
      const value = claims[claim];
      if (typeof value === 'string' && value) return `${claim}:${value}`;
      if (typeof value === 'number') return `${claim}:${value}`;
    }
    return null;
  } catch {
    // Not a JWT, or not decodable here. The caller falls back to the raw
    // token, which is the conservative direction.
    return null;
  }
}

/**
 * FNV-1a. Not cryptographic and does not need to be: this only has to
 * distinguish identities and avoid writing the token itself to storage.
 */
function hash(value: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}
